import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { DealerFeature, TrackingFeature } from '..';
import { DealerApiService } from '../../api/dealer-api.service';
import { actions } from '../services';

@Injectable()
export class ServicesEffects {
    actions$ = inject(Actions);
    api = inject(DealerApiService);
    store = inject(Store);

    getServicesHero$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadServicesHero),
            concatLatestFrom(() => this.store.select(DealerFeature.state.selectDealerSlug)),
            switchMap(([, slug]) =>
                from(this.api.getServicesHero(slug)).pipe(
                    map((servicesHero) => {
                        return actions.loadServicesHeroSuccess({ servicesHero });
                    }),
                    catchError(({ error }) => of(actions.loadGeneralServicesFailure({ error }))),
                ),
            ),
        );
    });

    getGeneralServices$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadGeneralServices),
            concatLatestFrom(() => this.store.select(DealerFeature.state.selectDealerSlug)),
            switchMap(([, slug]) =>
                from(this.api.getGeneralServices(slug)).pipe(
                    map((generalServices) => {
                        return actions.loadGeneralServicesSuccess({ generalServices });
                    }),
                    catchError(({ error }) => of(actions.loadGeneralServicesFailure({ error }))),
                ),
            ),
        );
    });

    getServiceFormContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadServiceForm),
            concatLatestFrom(() => this.store.select(DealerFeature.state.selectDealerSlug)),
            switchMap(([{ branchSlug }, slug]) => {
                return from(this.api.getServiceForm(slug, branchSlug)).pipe(
                    map((serviceFormContent) => {
                        return actions.loadServiceFormSuccess({ serviceFormContent });
                    }),
                    catchError(({ error }) => of(actions.loadServiceFormFailure({ error }))),
                );
            }),
        );
    });

    submitServiceForm$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.submitServiceForm),
            switchMap(({ payload }) =>
                from(this.api.postServiceForm(payload)).pipe(
                    switchMap(() => {
                        return [
                            actions.submitServiceFormResponse({ success: true }),
                            TrackingFeature.actions.eventTrackingPush({
                                dataLayer: {
                                    event: 'service_request_complete',
                                },
                            }),
                        ];
                    }),
                    catchError(() => {
                        return of(actions.submitServiceFormResponse({ success: false }));
                    }),
                ),
            ),
        );
    });

    submitServiceFormLoading$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.submitServiceForm),
            concatLatestFrom(() => this.store.select(DealerFeature.state.selectDealerSlug)),
            map(() => {
                return actions.submitServiceFormLoading({ loading: true });
            }),
        );
    });
}
